import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { isEmpty } from 'lodash'
import { getUnprocessableErrors } from '@10xlabs/utils'
import { Formik } from 'formik'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { Row } from 'components/v2/Row'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { ApolloError } from '@apollo/client'
import { usePushFileToS3 } from 'utils/file'
import Modal from 'components/v2/Modal'
import { IPhyisicalSimCsvFormValues } from 'features/BusinessClients/types'
import Tooltip from 'components/v2/Tooltip'
import { TableIcon } from 'components/Icons/TableIcon'
import { getFileNameWithoutExtension } from 'utils/file'

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledDownloadBtn = styled.a`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #a86eeb;
`

const StyledFormContent = styled(FormLayout.Content)`
  height: 100%;
`

const ActionsContainer = styled.div`
  margin-top: auto;
  margin-inline: auto;
`

const HeaderContainer = styled.div`
  display: flex;
  gap: 8px;
`

interface PhysicalSimsModalProps {
  setIsOpen: (value: boolean) => void
  onSubmit: ({ filePath, file }: { filePath: string; file: File }) => void
}

const TEMPLATE_CSV_FILE = 'data:text/csv;base64,bXNpc2RuLGljY19pZCxpbXNpLG9mZmVyX2lkLHN0YXR1cyxhY3RpdmVfZGF0ZQ=='

export const PhisicalSimsCSVModal = ({ setIsOpen, onSubmit }: PhysicalSimsModalProps): ReactElement => {
  const initialFormValues = {
    file: '',
    filePath: '',
    name: ''
  }

  const pushFileToS3 = usePushFileToS3()
  const [fileUploadLoading, setFileUploadLoading] = useState(false)
  const [error, setError] = useState<ApolloError | undefined>()

  const handleSubmit = async (formValues: IPhyisicalSimCsvFormValues): Promise<void> => {
    try {
      setError(undefined)
      setFileUploadLoading(true)
      const path = await pushFileToS3(formValues.file as File)
      setFileUploadLoading(false)

      if (path) {
        setIsOpen(false)
        onSubmit({
          filePath: path,
          file: formValues.file as File
        })
      }
    } catch (error) {
      // @ts-expect-error FIXME
      setError(error)
      setFileUploadLoading(false)
    }
  }

  return (
    <Modal.Container onClose={(): void => setIsOpen(false)}>
      <TimelineWrapper>
        <Modal.Header>
          <HeaderContainer>
            <Heading.H5>
              <FormattedMessage id="physicalSimsCSVForm.uploadCsv.title" defaultMessage="Add Physical Sims" />
            </Heading.H5>
            <Tooltip
              position="right"
              content={
                <FormattedMessage id="physicalSimsCSVForm.uploadCsv.tooltip" defaultMessage="Download csv template" />
              }
            >
              <StyledDownloadBtn href={TEMPLATE_CSV_FILE} download="physical_sims_template.csv">
                <TableIcon />
              </StyledDownloadBtn>
            </Tooltip>
          </HeaderContainer>
        </Modal.Header>
        <Formik initialValues={initialFormValues} onSubmit={handleSubmit}>
          {({ handleSubmit, values, setValues, setFieldValue, touched, errors }): ReactElement => {
            const handleFileChange = async (file: File | ''): Promise<void> => {
              if (!file) {
                setValues({ ...values, file: '' })
                return
              } else {
                // @ts-expect-error FIXME
                setValues({ ...values, file: file, name: getFileNameWithoutExtension(file) })
              }
            }

            return (
              <StyledFormContent as="form" onSubmit={handleSubmit}>
                <Row>
                  <Column>
                    <InputFile
                      touched={touched.file}
                      errors={errors.file}
                      onChange={handleFileChange}
                      accept={{
                        'text/csv': []
                      }}
                    />
                    {!isEmpty(values.file) && (
                      <FilePreview
                        // @ts-expect-error FIXME
                        file={values.file as File}
                        // @ts-expect-error FIXME
                        filename={(values.file as File).name}
                        onRemoveFile={(): void => {
                          setFieldValue('file', '')
                        }}
                      />
                    )}
                  </Column>
                </Row>
                <ActionsContainer>
                  {error && (
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="physicalSimsCSVForm.form.error"
                        defaultMessage="There was an error while creating your Customer"
                      />
                    </ErrorMessage>
                  )}
                  <FormLayout.Actions>
                    <Button kind="cancel" onClick={(): void => setIsOpen(false)}>
                      <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button type="submit" kind="primary" disabled={isEmpty(values.file)} loading={fileUploadLoading}>
                      <FormattedMessage id="dictionary.upload" defaultMessage="Upload" />
                    </Button>
                  </FormLayout.Actions>
                </ActionsContainer>
              </StyledFormContent>
            )
          }}
        </Formik>
      </TimelineWrapper>
    </Modal.Container>
  )
}
